import { HImg, HQLine, JoinButton, Kicker, Portrait, initials, split, useCopy } from "./HelpersUI";
import { FounderCard } from "./HelpersExtras";

function Row({ id, label, title, body, slots, flip, dark }) {
  return (
    <section data-testid={`helpers-solution-${id}`} className={`py-12 lg:py-16 ${dark ? "bg-[color:var(--h-ink)] text-white" : ""}`}>
      <div className="mx-auto grid max-w-7xl items-center gap-8 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
        <div className={`grid gap-4 ${slots.length > 1 ? "sm:grid-cols-2" : ""} ${flip ? "lg:order-2" : ""}`}>
          {slots.map((s) => <HImg key={s} slot={s} alt={label} sizes="(min-width: 1024px) 50vw, 100vw" className="aspect-[4/3] w-full rounded-[2rem]" />)}
        </div>
        <div>
          <p className={`mb-3 text-xs font-semibold uppercase tracking-[0.2em] ${dark ? "text-[color:var(--h-mint)]" : "text-[color:var(--h-mint-deep)]"}`}>{label}</p>
          {title && <h2 className="h-display mb-3 text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>}
          <p className={`max-w-lg text-base leading-relaxed sm:text-lg ${dark ? "text-white/75" : "text-slate-600"}`}>{body}</p>
        </div>
      </div>
    </section>
  );
}

export function SolutionsPage() {
  const { c } = useCopy();
  return (
    <div data-testid="helpers-solutions">
      <header className="mx-auto max-w-7xl px-5 pb-4 pt-12 sm:px-8 lg:pt-20">
        <h1 className="h-display max-w-3xl text-4xl font-extrabold tracking-tight text-[color:var(--h-ink)] sm:text-5xl lg:text-6xl">{c.sol_title}</h1>
      </header>
      <Row id="home" label="Home" body={c.sol_home} slots={["helpers_03_kitchen", "helpers_04_nursery"]} />
      <Row id="schools" label="Schools" body={c.sol_schools} slots={["helpers_05_crossing"]} flip />
      <Row id="workplace" label="Workplace" body={c.sol_workplace} slots={["helpers_06_office"]} />
      <Row id="health" label="Health" body={c.sol_health} slots={["helpers_07_hospital"]} flip />
      <Row id="cities" label="Cities" body={c.sol_cities} slots={["helpers_11_city"]} />
      <Row id="defense" label="Government & Defense" title={c.sol_defense_tag} body={c.sol_defense} slots={["helpers_09_defense"]} flip dark />
    </div>
  );
}

export function ForeverPage({ join }) {
  const { c, cfg } = useCopy();
  const baked = !!cfg?.helpers_images?.helpers_15_forever;  // the uploaded image carries the tagline as a caption
  return (
    <section data-testid="helpers-forever" className="mx-auto max-w-6xl px-5 py-12 sm:px-8 lg:py-16">
      <div className="overflow-hidden rounded-[2.5rem] bg-[color:var(--h-peach-soft)]">
        <HImg slot="helpers_15_forever" alt={`${c.forever_title} — ${c.forever_tag}`} eager sizes="(min-width: 1152px) 1152px, 100vw" fit="contain" className="aspect-video w-full" />
      </div>
      <div className="h-rise mx-auto mt-10 max-w-2xl text-center">
        <Kicker>{c.forever_title}</Kicker>
        <h1 data-testid="helpers-forever-tagline" className={baked ? "sr-only" : "h-display text-4xl font-extrabold leading-[1.08] tracking-tight text-[color:var(--h-ink)] sm:text-5xl"}>{c.forever_tag}</h1>
        <p className={`${baked ? "" : "mt-6"} text-base leading-relaxed text-slate-600 sm:text-lg`}>{c.forever_body}</p>
        <div className="mt-9"><JoinButton testid="helpers-forever-reserve-button" onClick={() => join(c.forever_button)}>{c.forever_button}</JoinButton></div>
      </div>
    </section>
  );
}

function BoardCard({ n, slot }) {
  const { c } = useCopy();
  const [name, role, bio] = split(c[`board_${n}`], 3);
  return (
    <article data-testid={`helpers-board-${n}`} className="h-card rounded-[1.75rem] bg-white p-7">
      <Portrait slot={slot} name={name} fallback={initials(name)} />
      <h3 className="h-display text-lg font-bold text-[color:var(--h-ink)]">{name}</h3>
      <p className="text-sm font-medium text-[color:var(--h-mint-deep)]">{role}</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{bio}</p>
    </article>
  );
}

const BOARD_SLOTS = ["helpers_17_board_hale", "helpers_18_board_okafor", "helpers_19_board_lindqvist", "helpers_20_board_reyes", "helpers_21_board_nakamura"];

export function AboutPage({ careers }) {
  const { c } = useCopy();
  return (
    <div data-testid="helpers-about">
      <section className="mx-auto max-w-7xl px-5 pt-12 sm:px-8 lg:pt-20">
        <h1 data-testid="helpers-factory-title" className="h-display max-w-3xl text-4xl font-extrabold tracking-tight text-[color:var(--h-ink)] sm:text-5xl lg:text-6xl">{c.factory_title}</h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{c.factory_body}</p>
        <HImg slot="helpers_10_factory" alt={c.factory_title} sizes="100vw" className="mt-10 aspect-[4/3] w-full rounded-[2.5rem] sm:aspect-[21/9]" />
      </section>
      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:py-24">
        <div className="grid items-end gap-8 lg:grid-cols-[1fr_1.2fr]">
          <h2 className="h-display text-3xl font-bold tracking-tight text-[color:var(--h-ink)] sm:text-4xl">{c.board_title}</h2>
          <HImg slot="helpers_12_board" alt={c.board_title} className="aspect-[16/9] w-full rounded-[2rem]" />
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {BOARD_SLOTS.map((slot, i) => <BoardCard key={slot} n={i + 1} slot={slot} />)}
          <FounderCard />
        </div>
      </section>
      <section className="bg-[color:var(--h-mint-soft)] py-16 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16">
          <HImg slot="helpers_13_careers" alt={c.careers_title} className="aspect-[4/3] w-full rounded-[2rem]" />
          <div>
            <h2 className="h-display text-3xl font-bold tracking-tight text-[color:var(--h-ink)] sm:text-4xl">{c.careers_title}</h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-slate-600">{c.careers_body}</p>
            <div className="mt-8"><JoinButton variant="ghost" testid="helpers-careers-button" onClick={careers}>{c.careers_button}</JoinButton></div>
          </div>
        </div>
      </section>
      <section data-testid="helpers-contact" className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <h2 className="h-display text-2xl font-bold text-[color:var(--h-ink)]">{c.contact_title}</h2>
        <p data-testid="helpers-contact-body" className="mt-3 text-base text-slate-600">{c.contact_body}</p>
        <HQLine className="mt-1 text-base text-slate-600" testid="helpers-contact-hq" />
      </section>
    </div>
  );
}

export function TestimonialsPage() {
  const { c } = useCopy();
  return (
    <section data-testid="helpers-testimonials" className="mx-auto max-w-7xl px-5 py-12 sm:px-8 lg:py-20">
      <h1 className="h-display max-w-3xl text-4xl font-extrabold tracking-tight text-[color:var(--h-ink)] sm:text-5xl lg:text-6xl">{c.testi_title}</h1>
      <div className="mt-10 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        <HImg slot="helpers_14_testimonial" alt={c.testi_title} className="aspect-[4/3] w-full rounded-[2.5rem] lg:aspect-auto lg:h-full" />
        <div className="grid gap-5">
          {[1, 2, 3].map((n) => {
            const [quote, who] = split(c[`testi_${n}`], 2);
            return (
              <figure key={n} data-testid={`helpers-testimonial-${n}`} className={`h-card rounded-[1.75rem] p-7 ${n === 2 ? "bg-[color:var(--h-peach-soft)]" : "bg-white"}`}>
                <blockquote className="h-display text-lg font-medium leading-snug text-[color:var(--h-ink)] sm:text-xl">“{quote}”</blockquote>
                <figcaption className="mt-4 text-sm font-medium text-slate-500">{who}</figcaption>
              </figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
